/**
 * certificateFormat — Phase 4B shared helpers for the collector-first
 * certificate experience.
 *
 * Every helper here is a pure, side-effect-free formatter. NO backend calls.
 * The friendly album mapping and the certificate template URLs are frontend-
 * only presentation concerns — the underlying DB values are never mutated.
 */
import { getCertificateAssets } from "@/data/certificateAssets";
import { getAlbum } from "@/config/albumCatalog";

/**
 * Friendly display names for album slugs. Certificate pages, cards and the
 * verification page all read from this map so the same short, human-readable
 * label is used everywhere.
 *
 * The DB slug and the frontend label are DECOUPLED on purpose: adding a new
 * label variant NEVER requires a DB migration.
 */
const FRIENDLY_ALBUM_NAMES = {
  "can-mex-usa-2026": "2026 World Cup - CAN/MEX/USA",
  // Collectibles catalog uses a different slug for the same physical album;
  // keep both mapped so any surface stays consistent.
  "panini-fifa-world-cup-2026": "2026 World Cup - CAN/MEX/USA",
  "qatar-2022": "2022 World Cup - Qatar",
  "russia-2018": "2018 World Cup - Russia",
  "brazil-2014": "2014 World Cup - Brazil",
  "south-africa-2010": "2010 World Cup - South Africa",
  "germany-2006": "2006 World Cup - Germany",
  "south-korea-japan-2002": "2002 World Cup - South Korea / Japan",
  "france-1998": "1998 World Cup - France",
  "usa-1994": "1994 World Cup - USA",
  "italy-1990": "1990 World Cup - Italy",
  "mexico-1986": "1986 World Cup - Mexico",
  "spain-1982": "1982 World Cup - Spain",
  "argentina-1978": "1978 World Cup - Argentina",
  "germany-1974": "1974 World Cup - Germany",
  "mexico-1970": "1970 World Cup - Mexico",
};

/**
 * @param {string|null|undefined} slug
 * @returns {string} friendly album name, or the catalog label, or "" if unknown.
 */
export function friendlyAlbumName(slug) {
  if (!slug) return "";
  const key = String(slug).trim().toLowerCase();
  if (FRIENDLY_ALBUM_NAMES[key]) return FRIENDLY_ALBUM_NAMES[key];
  const album = getAlbum(key);
  return album?.label || key;
}

/**
 * Cover type readable label — "Hardcover" / "Softcover" / "".
 * @param {string|null|undefined} coverType
 */
export function coverTypeLabel(coverType) {
  const key = String(coverType || "").toUpperCase();
  if (key === "HC") return "Hardcover";
  if (key === "SC") return "Softcover";
  return "";
}

/**
 * Normalise a certificate number for display. Trims + uppercases; returns
 * empty string when input is missing.
 * @param {string|null|undefined} certNumber
 */
export function formatCertNumber(certNumber) {
  return String(certNumber || "").trim().toUpperCase();
}

/**
 * Localised long-form date, e.g. "August 12, 2026". Returns "" on blank input.
 * Never throws.
 * @param {string|null|undefined} iso
 */
export function formatCompletionDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return String(iso); }
}

/**
 * Resolve the public-safe certificate template + album cover image URLs for
 * a given album/cover combination. Values are sourced from
 * `data/certificateAssets.js` — the same customer-uploaded artifacts used
 * elsewhere on the site — and are guaranteed to be the SAMPLE-watermarked
 * versions (never the owner's original printable PNG).
 *
 * Returns null when no artwork is available so callers can render a graceful
 * fallback state.
 *
 * @param {string|null|undefined} albumSlug
 * @param {"HC"|"SC"|string|null|undefined} albumType
 * @returns {{ certificateImageUrl:string, albumCoverUrl:string, certificateAlt:string, albumAlt:string } | null}
 */
export function certificateTemplateAssets(albumSlug, albumType) {
  const assets = getCertificateAssets(albumSlug, albumType);
  if (!assets) return null;
  return {
    certificateImageUrl: assets.certificate,
    albumCoverUrl: assets.albumCover,
    certificateAlt: assets.certificateAlt,
    albumAlt: assets.albumAlt,
  };
}
